import { getAvailabilityOptions } from "../../../utils/userConfig";

/**
 * FEAT-044 (D21 / GH #124) — Unified People (staff) management config.
 *
 * One role-filtered view over `peoples` (type=user) that replaces the per-role Responders /
 * Super Admins pages. Differs from those configs in two ways:
 *   1. The Role (permission) field is SELECTABLE on add (any built-in role) — so school/district
 *      admins and super-admins can be created from one place (closes the prior management gap).
 *   2. A Role FILTER is added so admins can slice the list by role.
 * Scope (district/school) filtering + server-side tenant scoping are unchanged.
 *
 * NOTE: the Role column shows `permission` today; multi-role (`roleIds`) chip rendering + a
 * roleIds-aware filter are follow-ups (see #124). Custom roles are managed on the Roles screen.
 */

// Built-in roles (mirror sam/backend/disclosure/base/roles.py BUILTIN_ROLES). Custom per-tenant
// roles are assigned via the Roles screen; this list drives the add-form + Role filter.
const ROLE_ADD_OPTIONS = [
  { label: "Responder", value: "responder" },
  { label: "School Admin", value: "schoolAdmin" },
  { label: "District Admin", value: "districtAdmin" },
  { label: "Super Admin", value: "disclosureAdmin" },
];

const ROLE_FILTER_OPTIONS = [
  { label: "Responder", id: "responder" },
  { label: "School Admin", id: "schoolAdmin" },
  { label: "District Admin", id: "districtAdmin" },
  { label: "Super Admin", id: "disclosureAdmin" },
];

export const getPeopleColumns = async () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return [
    {
      field: "firstName",
      secondField: "lastName",
      fieldConfig: "combined",
      headerName: "User Name",
      showColumn: true,
      cellType: "userName",
      showAdd: false,
    },
    {
      field: "openIncidents",
      headerName: "Open Incidents",
      cellType: "text",
      showColumn: true,
      showAdd: false,
    },
    {
      field: "schoolName",
      headerName: "School",
      cellType: "text",
      showColumn: true,
      showAdd: false,
    },
    {
      field: "permission",
      headerName: "Role",
      cellType: "chip",
      typeConfig: {
        colors: {
          "District Admin": "lightblue",
          "School Admin": "yellow",
          Responder: "blue",
          "Super Admin": "lightpurple",
        },
      },
      displayConfig: {
        districtAdmin: "District Admin",
        schoolAdmin: "School Admin",
        responder: "Responder",
        disclosureAdmin: "Super Admin",
      },
      showColumn: true,
      showAdd: true,
      required: true,
      addDefaultValue: "responder",
      type: "select",
      dataType: "string",
      options: ROLE_ADD_OPTIONS,
    },
    {
      field: "availability",
      headerName: "Availability",
      cellType: "Availability",
      typeConfig: {
        colors: { Available: "green", "Out of Office": "red" },
      },
      showColumn: true,
      showAdd: false,
    },
    {
      field: "firstName",
      headerName: "First Name",
      cellType: "text",
      showColumn: false,
      showAdd: true,
      required: true,
      type: "text",
      dataType: "string",
    },
    {
      field: "lastName",
      headerName: "Last Name",
      cellType: "text",
      showColumn: false,
      showAdd: true,
      required: true,
      type: "text",
      dataType: "string",
    },
    {
      field: "email",
      headerName: "Email",
      cellType: "text",
      showColumn: false,
      showAdd: true,
      required: true,
      type: "text",
      dataType: "email",
    },
    {
      field: "phone",
      headerName: "Phone",
      cellType: "text",
      showColumn: false,
      showAdd: true,
      required: true,
      type: "text",
      dataType: "phone",
    },
    {
      field: "district",
      headerName: "District",
      cellType: "text",
      showColumn: false,
      showAdd: true,
      required: true,
      showPermissions: ["disclosureAdmin"],
      addDefaultValue: user.district,
      type: "apiSelect",
      apiConfig: { apiBody: {}, label: "District", dataType: "districts" },
      dataType: "string",
    },
    {
      field: "school",
      headerName: "School",
      cellType: "text",
      showPermissions: ["disclosureAdmin", "districtAdmin"],
      showColumn: false,
      showAdd: true,
      required: true,
      type: "apiSelect",
      apiConfig: {
        apiBody: { filters: [{ key: "district", value: "{district}", expression: "=", valueObj: "filterValues" }] },
        label: "School",
        dataType: "schools",
      },
      dataType: "string",
      requires: { district: {} },
    },
  ];
};

export const getPeopleFilters = async () => {
  const availabilityOptions = await getAvailabilityOptions();
  return [
    {
      title: "Role",
      field: "permission",
      returnType: "list",
      options: ROLE_FILTER_OPTIONS,
      type: "select",
    },
    {
      title: "District",
      field: "district",
      labelField: "districtName",
      type: "apiSelect",
      returnType: "string",
      permission: ["disclosureAdmin"],
      apiConfig: { apiBody: {}, label: "District", dataType: "districts" },
    },
    {
      title: "School",
      field: "school",
      labelField: "schoolName",
      type: "apiSelect",
      permission: ["disclosureAdmin", "districtAdmin"],
      returnType: "string",
      apiConfig: {
        apiBody: { filters: [{ key: "district", value: "{district}", expression: "=", valueObj: "filterValues" }] },
        dataType: "schools",
        label: "School",
        requires: ["district"],
      },
    },
    {
      title: "Availability",
      field: "availability",
      returnType: "list",
      options: availabilityOptions,
      type: "select",
    },
  ];
};
